const clientSessionIdKey = "clientSessionId";
const lastChunkReloadAtKey = "lastChunkReloadAt";

function _setItem<T>(key: string, value: T): void {
	try {
		const serializedValue = JSON.stringify(value);
		sessionStorage.setItem(key, serializedValue);
	} catch (error) {
		console.error(`Failed to set ${key} in sessionStorage:`, error);
	}
}

function _getItem<T>(key: string): T | null {
	try {
		const item = sessionStorage.getItem(key);
		return item ? JSON.parse(item) : null;
	} catch (error) {
		console.error(`Failed to parse ${key} from sessionStorage:`, error);
		return null;
	}
}

function _removeItem(key: string): void {
	try {
		sessionStorage.removeItem(key);
	} catch (error) {
		console.error(`Failed to remove ${key} from sessionStorage:`, error);
	}
}

function hasItem(key: string): boolean {
	return sessionStorage.getItem(key) !== null;
}

function clear(): void {
	try {
		sessionStorage.clear();
	} catch (error) {
		console.error("Failed to clear sessionStorage:", error);
	}
}

// Per-tab UUID used by the backend to distinguish concurrent sessions of the same user
// (e.g. so changes made in tab A trigger a refresh indicator in tab B).
// sessionStorage is per-tab by design, so each tab gets its own id automatically.
function getOrCreateClientSessionId(): string {
	const existing = _getItem<string>(clientSessionIdKey);
	if (existing) return existing;
	const fresh = crypto.randomUUID();
	_setItem(clientSessionIdKey, fresh);
	return fresh;
}

// When this tab last reloaded to recover from a failed lazy-chunk load (epoch ms). Per-tab, so one
// tab's reload doesn't stop another tab from recovering. Null when it never did or storage failed.
function getLastChunkReloadAt(): number | null {
	const stored = _getItem<unknown>(lastChunkReloadAtKey);
	// DEFAULT: a value this code didn't write counts as no reload yet, so it can't block recovery.
	if (typeof stored !== "number" || !Number.isFinite(stored)) return null;
	return stored;
}

function setLastChunkReloadAt(epochMs: number): void {
	_setItem(lastChunkReloadAtKey, epochMs);
}

/**
 * SessionStorageService provides a simple interface for managing session storage.
 * Unlike LocalStorageService, sessionStorage is isolated per browser tab.
 * Avoid accessing sessionStorage directly in components - add your logic here instead and export the specific functions.
 */
export const sessionStorageService = {
	hasItem,
	clear,
	getOrCreateClientSessionId,
	getLastChunkReloadAt,
	setLastChunkReloadAt,
};
